import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function FiveCrownsScorecard() {
  const rounds = ['3s', '4s', '5s', '6s', '7s', '8s', '9s', '10s', 'Js', 'Qs', 'Ks'];

  // Pre-loaded game data from screenshots
  const initialPlayerCount = 8;
  const initialPlayers = ['Jayden', 'Allison', 'Melody', 'Kyler', 'Jordan', 'Erin', 'Abi', 'Mark'];
  const initialScores = [
    ['23', '24', '6', '0', '3', '33', '0', '0', '73', '0', '8'],    // Jayden: 170
    ['0', '5', '3', '14', '0', '10', '40', '21', '32', '28', '10'], // Allison: 163
    ['22', '0', '0', '14', '0', '39', '17', '13', '0', '14', '19'], // Melody: 138
    ['24', '27', '3', '20', '36', '0', '33', '16', '10', '0', '0'], // Kyler: 169
    ['17', '0', '8', '0', '3', '12', '29', '18', '5', '31', '11'], // Jordan: 134
    ['28', '35', '9', '23', '4', '15', '0', '3', '27', '11', '0'], // Erin: 155
    ['29', '8', '0', '3', '4', '0', '24', '4', '10', '0', '6'],    // Abi: 88
    ['22', '3', '0', '42', '19', '6', '13', '0', '4', '39', '17'], // Mark: 165
  ];

  const [playerCount, setPlayerCount] = useState(initialPlayerCount);
  const [players, setPlayers] = useState(initialPlayers);
  const [scores, setScores] = useState(initialScores);

  const [editingPlayer, setEditingPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('');
  const [shareStatus, setShareStatus] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const result = await window.storage.get('five-crowns-v9', true);
        if (result && result.value) {
          const data = JSON.parse(result.value);
          if (data.players) setPlayers(data.players);
          if (data.scores) setScores(data.scores);
          if (data.playerCount) setPlayerCount(data.playerCount);
        } else {
          // Save initial data if nothing stored
          await window.storage.set('five-crowns-v9', JSON.stringify({
            players: initialPlayers,
            scores: initialScores,
            playerCount: initialPlayerCount
          }), true);
        }
      } catch (err) {
        console.log('Using pre-loaded game');
      }
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    if (!loading) {
      const save = async () => {
        try {
          setSaveStatus('Saving...');
          await window.storage.set('five-crowns-v9', JSON.stringify({ players, scores, playerCount }), true);
          setSaveStatus('Saved ✓');
          setTimeout(() => setSaveStatus(''), 1500);
        } catch (err) {
          setSaveStatus('');
        }
      };
      save();
    }
  }, [scores, players, playerCount, loading]);

  const changePlayerCount = (count) => {
    let newPlayers = [...players];
    let newScores = [...scores];

    if (count > playerCount) {
      for (let i = playerCount; i < count; i++) {
        newPlayers.push(`Player ${i + 1}`);
        newScores.push(Array(11).fill(''));
      }
    } else {
      newPlayers = newPlayers.slice(0, count);
      newScores = newScores.slice(0, count);
    }

    setPlayerCount(count);
    setPlayers(newPlayers);
    setScores(newScores);
  };

  const updatePlayerName = (index, name) => {
    const newPlayers = [...players];
    newPlayers[index] = name || `Player ${index + 1}`;
    setPlayers(newPlayers);
    setEditingPlayer(null);
  };

  const updateScore = (playerIndex, roundIndex, value) => {
    const newScores = scores.map(row => [...row]);
    newScores[playerIndex][roundIndex] = value;
    setScores(newScores);
  };

  const getTotal = (playerIndex) => {
    return scores[playerIndex].reduce((sum, score) => sum + (parseInt(score) || 0), 0);
  };

  const hasAnyScores = () => {
    return players.slice(0, playerCount).some((_, i) => scores[i].some(s => s !== ''));
  };

  const isGameComplete = () => {
    return players.slice(0, playerCount).every((_, p) =>
      rounds.every((_, r) => scores[p][r] !== '')
    );
  };

  const getLeaderboard = () => {
    const playerData = players
      .slice(0, playerCount)
      .map((name, i) => ({ name, total: getTotal(i), index: i }));

    if (!hasAnyScores()) {
      return playerData;
    }

    return playerData.sort((a, b) => a.total - b.total);
  };

  const getWinners = () => {
    if (!isGameComplete()) return [];

    const leaderboard = getLeaderboard();
    const lowestScore = leaderboard[0].total;
    return leaderboard.filter(p => p.total === lowestScore);
  };

  const getRank = (playerIndex, leaderboard) => {
    if (!hasAnyScores()) return null;

    const playerTotal = getTotal(playerIndex);
    let rank = 0;
    let prevTotal = null;

    for (let i = 0; i < leaderboard.length; i++) {
      if (leaderboard[i].total !== prevTotal) {
        rank = i;
        prevTotal = leaderboard[i].total;
      }
      if (leaderboard[i].index === playerIndex) {
        return rank;
      }
    }
    return 0;
  };

  const getMedal = (rank) => {
    if (rank === 0) return '🥇';
    if (rank === 1) return '🥈';
    if (rank === 2) return '🥉';
    return `#${rank + 1}`;
  };

  const getCurrentRound = () => {
    for (let r = 0; r < rounds.length; r++) {
      const allFilled = players.slice(0, playerCount).every((_, p) => scores[p][r] !== '');
      if (!allFilled) return r;
    }
    return rounds.length;
  };

  const resetGame = () => {
    setScores(Array(playerCount).fill(null).map(() => Array(11).fill('')));
    setPlayers(Array(playerCount).fill(null).map((_, i) => `Player ${i + 1}`));
  };

  const shareScorecard = async () => {
    const leaderboard = getLeaderboard();
    const currentRound = getCurrentRound();
    const roundName = currentRound < rounds.length ? rounds[currentRound] : 'Complete';
    const currentDealer = currentRound % playerCount;
    const winners = getWinners();

    let text = `👑 FIVE CROWNS 👑\n`;

    if (winners.length > 0) {
      if (winners.length === 1) {
        text += `🏆 ${winners[0].name} WINS with ${winners[0].total} points! 🏆\n\n`;
      } else {
        text += `🏆 TIE GAME! 🏆\n`;
        text += `Winners: ${winners.map(w => w.name).join(' & ')} with ${winners[0].total} points!\n\n`;
      }
    } else {
      text += `Round: ${roundName}\n`;
      text += `🃏 ${players[currentDealer]} dealing\n\n`;
    }

    text += `🏆 STANDINGS:\n`;

    if (hasAnyScores()) {
      let prevTotal = null;
      let displayRank = 0;

      leaderboard.forEach((player, i) => {
        if (player.total !== prevTotal) {
          displayRank = i;
          prevTotal = player.total;
        }
        const medal = getMedal(displayRank);
        const tieIndicator = (i > 0 && player.total === leaderboard[i-1].total) ||
                           (i < leaderboard.length - 1 && player.total === leaderboard[i+1]?.total)
                           ? ' (tied)' : '';
        text += `${medal} ${player.name}: ${player.total}${tieIndicator}\n`;
      });
    } else {
      players.slice(0, playerCount).forEach((name, i) => {
        text += `${i + 1}. ${name}: –\n`;
      });
    }

    try {
      if (navigator.share) {
        await navigator.share({ text });
        setShareStatus('Shared!');
      } else {
        await navigator.clipboard.writeText(text);
        setShareStatus('Copied!');
      }
    } catch (err) {
      try {
        await navigator.clipboard.writeText(text);
        setShareStatus('Copied!');
      } catch (e) {
        setShareStatus('Failed');
      }
    }
    setTimeout(() => setShareStatus(''), 2000);
  };

  const leaderboard = getLeaderboard();
  const currentRound = getCurrentRound();
  const currentDealer = currentRound % playerCount;
  const gameHasScores = hasAnyScores();
  const gameComplete = isGameComplete();
  const winners = getWinners();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-emerald-900">
        <p className="text-amber-400 text-lg">Loading game...</p>
      </div>
    );
  }

  return (
    <div className="p-2 bg-gradient-to-br from-emerald-900 to-emerald-950 min-h-screen">
      {/* Back button */}
      <Link to="/" className="inline-block mb-2 text-emerald-300 hover:text-white text-sm">
        ← Back to Games
      </Link>

      <div className="text-center mb-2">
        <h1 className="text-xl font-bold text-amber-400">👑 Five Crowns 👑</h1>
        <p className="text-emerald-300 text-xs">
          {saveStatus || `Round: ${currentRound < rounds.length ? rounds[currentRound] : 'Complete'}`}
        </p>
        {!gameComplete && (
          <p className="text-amber-300 text-xs mt-1">
            🃏 {players[currentDealer]} dealing
          </p>
        )}
      </div>

      {/* Winner Banner */}
      {gameComplete && winners.length > 0 && (
        <div className="bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 rounded-xl p-4 mb-4 text-center animate-pulse">
          <div className="text-3xl mb-1">👑🎉👑</div>
          {winners.length === 1 ? (
            <>
              <div className="text-emerald-950 font-bold text-xl">{winners[0].name} WINS!</div>
              <div className="text-emerald-900 text-sm">Final Score: {winners[0].total} points</div>
            </>
          ) : (
            <>
              <div className="text-emerald-950 font-bold text-xl">TIE GAME!</div>
              <div className="text-emerald-900 text-sm">
                {winners.map(w => w.name).join(' & ')} tied with {winners[0].total} points!
              </div>
            </>
          )}
        </div>
      )}

      {/* Player Count Selector */}
      <div className="flex justify-center items-center gap-2 mb-3 flex-wrap">
        <span className="text-emerald-300 text-xs">Players:</span>
        <div className="flex gap-1 flex-wrap justify-center">
          {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map(num => (
            <button
              key={num}
              onClick={() => changePlayerCount(num)}
              className={`w-7 h-7 rounded-lg font-bold text-xs transition-all ${
                playerCount === num
                  ? 'bg-amber-500 text-emerald-950'
                  : 'bg-emerald-800 text-emerald-300 hover:bg-emerald-700'
              }`}
            >
              {num}
            </button>
          ))}
        </div>
      </div>

      {/* Leaderboard - Always visible */}
      <div className="bg-emerald-950/80 rounded-lg p-3 mb-2">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-amber-400 font-bold text-sm">🏆 LEADERBOARD</h2>
          <span className="text-emerald-400 text-xs bg-emerald-800 px-2 py-1 rounded">
            {gameComplete ? '✓ Complete' : `Round: ${rounds[currentRound]}`}
          </span>
        </div>
        <div className="space-y-1">
          {leaderboard.map((player, i) => {
            const rank = getRank(player.index, leaderboard);
            const isLeader = rank === 0 && gameHasScores;
            const isTied = gameHasScores && i > 0 && player.total === leaderboard[i-1].total;
            const isWinner = winners.some(w => w.index === player.index);

            return (
              <div
                key={player.index}
                className={`flex items-center justify-between px-3 py-1.5 rounded ${
                  !gameHasScores ? 'bg-emerald-800/30' :
                  isWinner && gameComplete ? 'bg-amber-500/40 border border-amber-400' :
                  rank === 0 ? 'bg-amber-500/30' :
                  rank === 1 ? 'bg-gray-400/20' :
                  rank === 2 ? 'bg-orange-600/20' :
                  'bg-emerald-800/30'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">
                    {!gameHasScores ? `${i + 1}.` : isWinner && gameComplete ? '👑' : getMedal(rank)}
                  </span>
                  <span className={`font-medium ${isLeader ? 'text-amber-300' : 'text-white'}`}>
                    {player.name}
                    {isTied && <span className="text-xs text-emerald-400 ml-1">(tied)</span>}
                  </span>
                </div>
                <span className={`font-bold font-mono text-lg ${isLeader ? 'text-amber-300' : 'text-white'}`}>
                  {gameHasScores ? player.total : '–'}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Share & TV Buttons */}
      <div className="flex justify-center gap-2 mb-2">
        <button
          onClick={shareScorecard}
          className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          📤 {shareStatus || 'Share Standings'}
        </button>
        <button
          onClick={() => window.open('/five-crowns/tv', '_blank')}
          className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          📺 TV Mode
        </button>
      </div>

      {/* Score Table */}
      <div className="overflow-x-auto rounded-lg">
        <table className="text-sm border-collapse">
          <thead>
            <tr className="bg-emerald-950">
              <th className="p-1.5 text-amber-400 text-xs sticky left-0 bg-emerald-950 z-10 w-12">Wild</th>
              {players.slice(0, playerCount).map((player, i) => {
                const rank = getRank(i, leaderboard);
                const isDealer = currentDealer === i && !gameComplete;
                const isLeader = rank === 0 && gameHasScores;
                const isWinner = winners.some(w => w.index === i);
                return (
                  <th key={i} className={`p-1 min-w-[80px] max-w-[120px] transition-all ${isDealer ? 'bg-amber-500/20' : ''} ${isWinner && gameComplete ? 'bg-amber-500/30' : ''}`}>
                    {editingPlayer === i ? (
                      <input
                        type="text"
                        value={player}
                        onChange={(e) => {
                          const newPlayers = [...players];
                          newPlayers[i] = e.target.value;
                          setPlayers(newPlayers);
                        }}
                        onBlur={() => updatePlayerName(i, players[i])}
                        autoFocus
                        className="w-full bg-emerald-700 text-white text-center text-xs rounded p-1"
                      />
                    ) : (
                      <button
                        onClick={() => setEditingPlayer(i)}
                        className={`w-full text-xs font-medium p-1 rounded transition-colors ${
                          isWinner && gameComplete ? 'text-amber-300 font-bold' :
                          isDealer ? 'text-amber-300 font-bold' :
                          isLeader ? 'text-amber-300 bg-amber-900/40' : 'text-white'
                        }`}
                      >
                        {isWinner && gameComplete ? '👑 ' : isDealer ? '🃏 ' : ''}{isLeader && !gameComplete ? '👑 ' : ''}{player}
                      </button>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rounds.map((round, ri) => (
              <tr key={round} className={`border-t border-emerald-700/30 ${ri === currentRound && !gameComplete ? 'bg-emerald-700/20' : ''}`}>
                <td className={`p-1.5 font-bold text-xs text-center sticky left-0 z-10 ${ri === currentRound && !gameComplete ? 'bg-emerald-800 text-amber-200' : 'bg-emerald-900 text-amber-300'}`}>
                  {ri === currentRound && !gameComplete ? '▶ ' : ''}{round}
                </td>
                {players.slice(0, playerCount).map((_, pi) => {
                  const isDealer = currentDealer === pi && !gameComplete;
                  const scoreValue = scores[pi][ri];
                  const isZero = scoreValue === '0';
                  const isWinner = winners.some(w => w.index === pi);
                  return (
                    <td key={pi} className={`p-0.5 ${isDealer ? 'bg-amber-500/10' : ''} ${isWinner && gameComplete ? 'bg-amber-500/10' : ''}`}>
                      <input
                        type="number"
                        inputMode="numeric"
                        value={scoreValue}
                        onChange={(e) => updateScore(pi, ri, e.target.value)}
                        className={`w-full text-center rounded p-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-amber-400 ${
                          isZero
                            ? 'bg-green-500/30 text-green-300 font-bold'
                            : 'bg-emerald-800/50 text-white'
                        }`}
                        placeholder="–"
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="bg-emerald-950 font-bold border-t-2 border-emerald-600">
              <td className="p-2 text-amber-400 text-xs sticky left-0 bg-emerald-950 z-10 w-12">TOTAL</td>
              {players.slice(0, playerCount).map((_, pi) => {
                const rank = getRank(pi, leaderboard);
                const isDealer = currentDealer === pi && !gameComplete;
                const isLeader = rank === 0 && gameHasScores;
                const isWinner = winners.some(w => w.index === pi);
                return (
                  <td key={pi} className={`p-2 text-center text-base ${isDealer ? 'bg-amber-500/10' : ''} ${isWinner && gameComplete ? 'bg-amber-500/20' : ''} ${isLeader ? 'text-amber-300' : 'text-white'}`}>
                    {getTotal(pi)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="flex justify-center mt-3">
        <button onClick={resetGame} className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
          New Game
        </button>
      </div>

      <p className="text-center text-emerald-400 text-xs mt-2">J=11 • Q=12 • K=13 • Joker=50</p>
    </div>
  );
}

export default FiveCrownsScorecard;
